import dayjs from 'dayjs';
import roundHalfEven from 'round-half-even';
import { ECOM_DISCOUNT_TYPE_ENUM } from '../config/constants';

const getInitialsFromName = (name) => {
  let initials = '';
  if (name?.length > 0) {
    const splitName = name.replace(/\s+/g, ' ').trim().split(' ');
    initials += splitName[0][0];
    if (splitName.length > 1) {
      initials += splitName[splitName.length - 1][0];
    }
  }
  return initials;
};

const isDarkText = (backgroundColor) => {
  if (!backgroundColor) {
    return false;
  }

  let color = backgroundColor.substring(1, backgroundColor.length);
  if (color.length === 3) {
    color += color;
  }

  const red = parseInt(color.substring(0, 2), 16);
  const green = parseInt(color.substring(2, 4), 16);
  const blue = parseInt(color.substring(4, 6), 16);
  return red * 0.299 + green * 0.587 + blue * 0.114 > 186;
};

const currencyFormat = (num) => {
  if (!Number.isNaN(parseFloat(num))) {
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    });
    return formatter.format(roundHalfEven(parseFloat(num), 2));
  }
  return '';
};

const formatUtcDate = (date) =>
  !date || typeof date === 'object' || date?.split(' ').length > 2
    ? date
    : `${date.charAt(date.length - 1) === 'Z' ? date : `${date}Z`}`;

const formatLocalDate = (date) => date?.replace('Z', '');

const formatUrl = (url) =>
  url.charAt(url.length - 1) === '/' ? url.substring(0, url.length - 1) : url;

const toTitleCase = (str) =>
  str?.replace(
    /\w\S*/g,
    (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
  );

const capitalizeFirstLetter = (str) =>
  typeof str === 'string' && str.length > 0
    ? str.charAt(0).toUpperCase() + str.slice(1)
    : str;

const singularPluralFormat = (value, singular, plural) =>
  `${value} ${value === 1 ? singular : plural}`;

const changeListItemSortOrder = (
  list,
  startIndex,
  endIndex,
  dataSelector = null
) => {
  if (startIndex === endIndex) {
    return list;
  }

  const selector = dataSelector || 'sort';
  const isGreaterIndex = endIndex > startIndex;

  const listCopy = [...list];
  for (const element of listCopy) {
    if (element[selector] === startIndex) {
      element[selector] = endIndex;
    }
    else if (
      isGreaterIndex &&
      element[selector] >= startIndex &&
      element[selector] <= endIndex
    ) {
      element[selector] -= 1;
    }
    else if (element[selector] <= startIndex && element[selector] >= endIndex) {
      element[selector] += 1;
    }
  }

  return listCopy;
};

const isUrlString = (inputString) =>
  /^(https?:\/\/[^\s$.?#].[^\s]*)$/.test(inputString);

const getDateRangeLabel = (start, end) =>
  start && end
    ? dayjs(start).year() === dayjs(end).year()
      ? `${dayjs(start).format('MMM DD')} - ${dayjs(end).format(
          'MMM DD, YYYY'
        )}`
      : `${dayjs(start).format('MMM DD, YYYY')} - ${dayjs(end).format(
          'MMM DD, YYYY'
        )}`
    : null;

const getDateOnly = (date) => {
  if (!date) {
    return null;
  }
  const d = new Date(date);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

const fixHtmlHrefUrls = (html) =>
  html.replace(/href="(.*?)"/g, (match, url) => {
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      url = `https://${url}`;
    }
    return `href="${url}" target="_blank"`;
  });

const createGoogleMapsLink = ({
  address1,
  city,
  state,
  postalCode,
  country
}) => {
  let address = `${address1}`;
  address += `, ${city}, ${state} ${postalCode}, ${country}`;
  const formattedAddress = address.replace(/\s+/g, '+');
  const googleMapsLink = `https://www.google.com/maps/place/${formattedAddress}`;

  return googleMapsLink;
};

const buildQueryString = (obj, fields) => {
  const params = [];
  fields.forEach((field) => {
    if (obj[field] !== undefined) {
      params.push(`${field}=${encodeURIComponent(obj[field])}`);
    }
  });
  return `?${params.join('&')}`;
};

const buildURLSearchParams = (searchQueryObject) => {
  const params = new URLSearchParams();

  for (const key of Object.keys(searchQueryObject)) {
    const value = searchQueryObject[key];
    if (value != null) {
      if (Array.isArray(value)) {
        value.forEach((v, i) => params.append(`${key}[${i}]`, v));
      }
      else {
        params.set(key, value);
      }
    }
  }

  return params;
};

const formatDataTypeString = (value, dataType) => {
  if (value || value === 0) {
    switch (dataType) {
      case 'currency':
        return currencyFormat(value / 100);
      case 'percentage':
        return `${Number(value)?.toFixed(2)}%`;
      case 'date':
        if (value.includes('-')) {
          return value;
        }
        return dayjs(value).format('MMM DD, YYYY');
      case 'number':
      default:
        return value;
    }
  }
  return '-';
};

const sterilizeUrlHandle = (str) =>
  str
    ?.trim()
    .toLowerCase()
    .replace(/ /g, '-')
    .replace(/[^a-zA-Z0-9-_]/g, '')
    .replace(/(-)\1+/g, '-') ?? '';

const roundUpToCentValue = (amount, centValue) => {
  if (centValue >= 0) {
    return Math.ceil(amount - centValue) + centValue;
  }
  return amount;
};

const calculateBundlePrice = (
  priceInCents,
  discountType,
  discountAmountInCents
) => {
  if (!Number.isFinite(priceInCents) || priceInCents <= 0) {
    return 0;
  }

  if (!Number.isFinite(discountAmountInCents) || discountAmountInCents < 0) {
    return priceInCents;
  }

  let finalPriceInCents;
  switch (discountType) {
    case ECOM_DISCOUNT_TYPE_ENUM.PERCENTAGE_DISCOUNT: {
      const discount = priceInCents * (discountAmountInCents / 100);
      finalPriceInCents = roundHalfEven(priceInCents - discount, 0);
      break;
    }

    case ECOM_DISCOUNT_TYPE_ENUM.FLAT_DISCOUNT: {
      finalPriceInCents = priceInCents - discountAmountInCents;
      break;
    }

    case ECOM_DISCOUNT_TYPE_ENUM.SET_PRICE: {
      finalPriceInCents = Math.min(discountAmountInCents, priceInCents);
      break;
    }

    default:
      return priceInCents;
  }
  return Math.max(finalPriceInCents, 0);
};

const calculateDiscountPercentage = (
  beforePrice,
  afterPrice,
  discountAmount
) => {
  if (typeof discountAmount === 'number' && beforePrice > 0) {
    return Math.round((discountAmount / beforePrice) * 100);
  }

  if (beforePrice > 0 && typeof afterPrice === 'number') {
    return Math.round(((beforePrice - afterPrice) / beforePrice) * 100);
  }

  return 0;
};

export {
  getInitialsFromName,
  isDarkText,
  currencyFormat,
  formatUtcDate,
  formatLocalDate,
  formatUrl,
  toTitleCase,
  capitalizeFirstLetter,
  singularPluralFormat,
  changeListItemSortOrder,
  roundUpToCentValue,
  sterilizeUrlHandle,
  getDateRangeLabel,
  createGoogleMapsLink,
  isUrlString,
  formatDataTypeString,
  getDateOnly,
  fixHtmlHrefUrls,
  buildQueryString,
  buildURLSearchParams,
  calculateBundlePrice,
  calculateDiscountPercentage
};
